import React, {Fragment, createContext} from 'react';
import {AlignerByScreen, FlexBoxComponent, FullScreenFader, RichText} from '@gongxigames/framework/core/fragments/react';
import {Container} from '@inlet/react-pixi';
import {
    RichTextTextStyle,
    RichText as RichTextClass,
    RowDirection,
    ScreenParameters,
    ModuleIsEnabledFilter, Installer, CoreDI, inject
} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {SymbolComponent} from '@gongxigames/framework/slot/modules/oddsUpfront/components/SymbolComponent';
import {
    OddsUpfrontSymbolsData,
    UpfrontOddsModuleEvents
} from '@gongxigames/framework/slot/modules/oddsUpfront/OddsUpfrontModule';
import {PayTable} from '@gongxigames/framework/slot/modules/oddsUpfront/components/PayTable';
import {TextAndButtonAccept} from '@gongxigames/framework/slot/modules/oddsUpfront/components/TextAndButtonAccept';
import {LoadingLayer} from '@gongxigames/framework/slot/modules/oddsUpfront/components/LoadingLayer';
import {GameConfig, ReactView, ScrollComponent} from '@gongxigames/framework/slot';

type TextStyle = Partial<RichTextTextStyle>;
const title = 'betPredictionTitle';
const titlePortrait = 'betPredictionTitleIdPortrait';
const titleLandscape = 'betPredictionTitleIdLandscape';
const accept = 'betPredictionAcceptText';
const acceptPortrait = 'betPredictionAcceptIdPortrait';
const acceptLandscape = 'betPredictionAcceptIdLandscape';

const style: TextStyle = {
    fontFamily: 'PF DinDisplay Pro',
    fontWeight: 'bold',
    fill: '#f48e0c'
};
const styleTitle: TextStyle = {...style, fill: 'white', align: 'center', fontSize: 75};

RichTextClass.setStyle(title, styleTitle);
RichTextClass.setStyle(titlePortrait, {...styleTitle, fontSize: 150});
RichTextClass.setStyle(titleLandscape, {...styleTitle, fontSize: 125});

export type OddsUpfrontViewState = {
    numberPerRow: number;
    scale: number;
    scrollHeight: number;
    direction: RowDirection;
    horizontalWidth: number;
    visible?: boolean;
    screenParameters?: ScreenParameters;
};

export type OddsUpfrontPayTableContextType = {
    visible: boolean;
    horizontalWidth: number;
    scale: number;
    style: TextStyle;
    symbols: OddsUpfrontSymbolsData;
    maxSymbolHeight: number;
    maxSymbolWidth: number;
    SymbolComponent: typeof SymbolComponent;
};

export const PayTableContext = createContext<OddsUpfrontPayTableContextType>({
    visible: true,
    horizontalWidth: 500,
    scale: 0.75,
    style,
    symbols: {},
    maxSymbolHeight: 0,
    maxSymbolWidth: 0,
    SymbolComponent
});

export class OddsUpfrontReactView extends ReactView<OddsUpfrontViewState, UpfrontOddsModuleEvents> {
    public static readonly NAME: string = 'UpfrontOddsReactView';
    public static layer = LAYERS.POPUPS;
    public static readonly isEnabled: ModuleIsEnabledFilter = (components: Installer) => {
        return components.getComponent<SlotSettings>(CoreDI.Settings).get('showOddsUpfront') || false;
    };

    public state: OddsUpfrontViewState = {
        visible: false,
        numberPerRow: 3,
        scale: 0.75,
        scrollHeight: 1720,
        direction: 'column',
        horizontalWidth: 500
    };

    protected _symbolsData!: OddsUpfrontSymbolsData;
    protected _wager = 1;
    protected _isWaysGame = false;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    public init(): void {
        super.init();
        this.initStyles();

        this.on('UpfrontOddsReactView.MODULE_INITIALIZED', this.moduleInitialized.bind(this), this);
    }

    protected initStyles(): void {
        const styleAccept: TextStyle = {...styleTitle,
            fontSize: 45,
            wordWrap: true,
            wordWrapWidth: this._gameConfig.mainFrame.width * 0.7,
            verticalAlign: 'center'
        };

        RichTextClass.setStyle(accept, styleAccept);
        RichTextClass.setStyle(acceptLandscape, {...styleAccept, fontSize: 75});
        RichTextClass.setStyle(acceptPortrait, {
            ...styleAccept,
            fontSize: 80,
            wordWrapWidth: this._gameConfig.mainFrame.height * 0.95,
            breakWords: true
        });
    }

    public setViewData({symbolsData, wager, isWaysGame}: {symbolsData: OddsUpfrontSymbolsData; wager: number; isWaysGame: boolean;}): void {
        this._symbolsData = symbolsData;
        this._wager = wager;
        this._isWaysGame = isWaysGame;
    }

    protected removeEvents(): void {
        super.removeEvents();
        this.off('UpfrontOddsReactView.MODULE_INITIALIZED', this);
    }

    public moduleInitialized(): void {
        this.setState({visible: true});
    }

    public onResized(screenParameters: ScreenParameters): void {
        this.setState({screenParameters});
        this._reactMediator.forceUpdate();
    }

    protected getDataByPlatformViewport<T>(
        mobilePortrait: T,
        mobileLandscape: T,
        desktop: T
    ): T {
        const isPortrait = this._screenManager.isPortraitOrientation();

        return this.isPlatformMobile() ? (isPortrait ? mobilePortrait : mobileLandscape) : desktop;
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this.setState(this.getNewStateData());
    }

    protected getNewStateData(): OddsUpfrontViewState {
        const {height} = this.getScreenSize();

        return {
            numberPerRow: this.getDataByPlatformViewport(1, 2, 3),
            scale: this.getDataByPlatformViewport(1.5, 1.25, 0.75),
            scrollHeight: this.getDataByPlatformViewport(height - 1000, height - 600, 1720),
            direction: this.getDataByPlatformViewport<RowDirection>('column', 'row', 'column'),
            horizontalWidth: this.getDataByPlatformViewport(1000, 700, 500)
        };
    }

    protected getScreenSize(): {height: number; width: number;} {
        const {height, width} = this.state.screenParameters?.getScreenBounds() ?? {height: 0, width: 0};

        return {height, width};
    }

    protected onButtonClicked(): void {
        this.emit('UpfrontOddsReactView.ACCEPT_CLICKED');
        this.soundPlayer.play('buttonclick');
    }

    public acceptReceived(): void {
        this.setState({visible: false});
        this.remove();
    }

    protected getPayTableInsertion(): { children: JSX.Element; index: number;} | undefined {
        return undefined;
    }

    protected getTitleStyleId(): string {
        return this.getDataByPlatformViewport(titlePortrait, titleLandscape, title);
    }

    protected getPayTableContextValue(): OddsUpfrontPayTableContextType {
        const {scale, numberPerRow, horizontalWidth} = this.state;
        const visible = numberPerRow > 1;
        const symbols = this._symbolsData;
        const maxSymbolHeight = this.getDataByPlatformViewport(1000, 1000, 372);
        const maxSymbolWidth = this.getDataByPlatformViewport(850, 0, 0);

        return {visible, horizontalWidth, scale, style, symbols, maxSymbolHeight, maxSymbolWidth, SymbolComponent};
    }

    protected getMainLayer(): JSX.Element {
        if (!this.state.screenParameters) {
            return <></>;
        }

        const styleTitle = this.getTitleStyleId();
        const styleAcceptButton = this.getDataByPlatformViewport(acceptPortrait, acceptLandscape, accept);
        const {width} = this.getScreenSize();
        const {numberPerRow, direction, scrollHeight, screenParameters} = this.state;
        const payTableProps = {isMobile: this.isPlatformMobile(), numberPerRow, insertion: this.getPayTableInsertion()};
        const maxHeight = this.isPlatformMobile() && this._screenManager.isPortraitOrientation() ? 525 : 0;

        return (
            <Fragment>
                <FullScreenFader screenParameters={screenParameters}/>
                <AlignerByScreen horizontalAlign={'center'} verticalAlign={'center'} >
                    <FlexBoxComponent
                        direction={'column'}
                        gap={this.isPlatformDesktop() ? 70 : 50}
                        justifyContent={'center'}
                        alignItems={'center'}
                    >
                        <FlexBoxComponent alignItems={'center'} direction={'column'}>
                            <RichText id={title} style={styleTitle} maxWidth={this._gameConfig.mainFrame.height * 0.9}/>
                        </FlexBoxComponent>

                        <FlexBoxComponent name='content' alignItems={'center'}>
                            <ScrollComponent boundsWidth={width} boundsHeight={scrollHeight} centeringContent={true}>
                                <FlexBoxComponent name='PayTableContent' alignItems={'center'} direction={'column'}>
                                    <PayTableContext.Provider value={this.getPayTableContextValue()}>
                                        <PayTable {...payTableProps}/>
                                    </PayTableContext.Provider>
                                    <FlexBoxComponent visible={this._wager !== 1}>
                                        <RichText
                                            id={this._isWaysGame ? 'betPredictionFormulaTextWays': 'betPredictionFormulaText'}
                                            style={styleAcceptButton}
                                            values={{wager: this._wager}}
                                        />
                                    </FlexBoxComponent>
                                </FlexBoxComponent>
                            </ScrollComponent>
                        </FlexBoxComponent>

                        <TextAndButtonAccept
                            textId={accept}
                            style={styleAcceptButton}
                            callback={this.onButtonClicked.bind(this)}
                            direction={direction}
                            maxHeight={maxHeight}
                        />
                    </FlexBoxComponent>
                </AlignerByScreen>
            </Fragment>
        );
    }

    render(): JSX.Element {
        const isLoading = !this.state.visible;
        const {width, height} = this.getScreenSize();

        return (
            <Container name={OddsUpfrontReactView.NAME.toString()} >
                {isLoading
                    ? <LoadingLayer width={width} height={height} style={style}/>
                    : this.getMainLayer()
                }
            </Container>
        );

    }
}
